import React, { useState } from 'react';
import { acceptInvite } from '@/services/user-invitations';
import { Trans } from '@lingui/react/macro';
import { Button } from '@/components/Buttons';
import { useSelector } from 'react-redux';
import { withRouter, RouteComponentProps } from 'react-router';
import { IState } from '@/state/IState';
import { InvitationWithToken, TranslatableText } from '@/types';
import { RegisterWithState } from './RegisterForm';
import { useAppDispatch, useQueryString } from '@/utils/hooks';
import LoginForm from './LoginForm';
import { loadUser } from '@/state/global/creators';
import { Button as RubusButton } from '@ygtechnologies/rubus-ui';
import { CustomLink } from '@ygtechnologies/bygr-frontend-component-library';

type OwnProps = RouteComponentProps<any> & {
    invitation: InvitationWithToken;
    greetingText: TranslatableText;
};

interface QueryStringParams {
    mode: PageMode;
}

enum PageMode {
    Login = 'login',
    Register = 'register',
}

const InvitationDetailsBuyerOrProfile: React.FC<OwnProps> = ({ invitation, history, location, greetingText }) => {
    const [acceptInProgress, setAcceptInProgress] = useState(false);
    const { mode } = useQueryString<QueryStringParams>(location);
    const user = useSelector((state: IState) => state.global.user!);
    const dispatch = useAppDispatch();

    const redirectUrl = `/profiles/${invitation.inviteeProfileId}`;

    async function handleAccept(e) {
        e.preventDefault();

        setAcceptInProgress(true);

        try {
            // const redirectUrl = `/profiles/${invitation.inviteeProfileId}`;
            await acceptInvite(invitation.token);
            await loadUser(dispatch);
            history.replace(redirectUrl);
        } finally {
            setAcceptInProgress(false);
        }
    }

    if (user.isLoggedIn) {
        if (invitation.inviteeEmail.toLowerCase() !== user.email.toLowerCase()) {
            return (
                <div className="text-center flex column">
                    {greetingText}

                    <RubusButton
                        as={CustomLink}
                        to={`/logout?returnUrl=${encodeURIComponent(location.pathname + location.search)}`}
                        variant="primary"
                        size="lg"
                        loading={acceptInProgress}>
                        <Trans>Log out and switch user</Trans>
                    </RubusButton>
                </div>
            );
        }

        return (
            <>
                <p className="text-center">{greetingText}</p>
                <Button color="primary" block onClick={handleAccept} size="xlarge" loading={acceptInProgress}>
                    <Trans>Accept invitation</Trans>
                </Button>
            </>
        );
    }

    if (mode === PageMode.Login || invitation.emailExists) {
        return (
            <>
                <div className="text-center">{greetingText}</div>

                <h3>
                    <Trans>Sign in</Trans>
                </h3>
                <LoginForm initialEmail={invitation.inviteeEmail} />

                <hr />

                <div>
                    <Trans>
                        Don't have an account?&nbsp;
                        <CustomLink to={{ ...location, query: { mode: PageMode.Register } }}>Create one here</CustomLink>
                    </Trans>
                </div>
            </>
        );
    }

    return (
        <>
            <div className="text-center">{greetingText}</div>

            <h3>
                <Trans>Create an account</Trans>
            </h3>
            <RegisterWithState invitation={invitation} onRegister={() => history.replace(redirectUrl)} />

            <hr />

            <div>
                <Trans>
                    Already have an account?&nbsp;
                    <CustomLink to={{ ...location, query: { mode: PageMode.Login } }}>Log in here</CustomLink>
                </Trans>
            </div>
        </>
    );
};

export default withRouter(InvitationDetailsBuyerOrProfile);
